import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { getDashboard, getRelatorio } from '../services/api';

// ─── paleta light ──────────────────────────────────────────────────────────
const C = {
  bg:      '#f1f5f9',
  card:    '#ffffff',
  card2:   '#f8fafc',
  border:  '#e2e8f0',
  green:   '#16a34a',
  yellow:  '#d97706',
  red:     '#dc2626',
  blue:    '#2563eb',
  indigo:  '#4f46e5',
  navy:    '#1e3a5f',
  cyan:    '#0891b2',
  text:    '#0f172a',
  muted:   '#64748b',
  muted2:  '#94a3b8',
};

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function fmtDia(d) {
  const [da, ma] = d.split('/');
  return `${da}/${ma}`;
}
function sortByDate(a, b) {
  const p = s => { const [dd,mm,yy] = s.split('/').map(Number); return new Date(yy, mm-1, dd); };
  return p(a) - p(b);
}
function initials(n) {
  return (n || '').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
}
function toHHMM(h) {
  if (!h && h !== 0) return '—';
  const neg = h < 0;
  const abs = Math.abs(h);
  const hh  = Math.floor(abs);
  const mm  = Math.round((abs - hh) * 60);
  return `${neg ? '-' : ''}${hh}h${String(mm).padStart(2,'0')}m`;
}

// ─── status → cor heatmap ─────────────────────────────────────────────────
function statusHeat(s) {
  if (!s)               return '#f1f5f9';
  if (s === 'NORMAL')   return C.green;
  if (s === 'FALTA')    return C.red;
  if (s === 'ATESTADO') return C.yellow;
  return '#94a3b8'; // FOLGA, FERIAS, FERIADO
}

// ─── tooltip customizado ──────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid #e2e8f0`, borderRadius: 8, padding: '10px 14px', fontSize: 'var(--fs-xs)', color: C.text, boxShadow: '0 4px 12px rgba(15,23,42,0.08)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: C.muted }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: C.muted }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Badge de score ────────────────────────────────────────────────────────
function ScoreBadge({ score }) {
  const color = score >= 85 ? C.green : score >= 65 ? C.yellow : C.red;
  const bg    = score >= 85 ? 'rgba(22,163,74,0.10)' : score >= 65 ? 'rgba(217,119,6,0.10)' : 'rgba(220,38,38,0.10)';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 48, height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 800, color, background: bg, borderRadius: 5, padding: '1px 6px' }}>{score}</span>
    </div>
  );
}

// ─── Card base ────────────────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', ...style }}>
      {children}
    </div>
  );
}

// ─── Section title ────────────────────────────────────────────────────────
function SectionTitle({ icon, title, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className={`bi ${icon}`} style={{ color: C.indigo, fontSize: '0.95rem' }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', color: C.text, lineHeight: 1 }}>{title}</div>
          {sub && <div style={{ fontSize: 'var(--fs-2xs)', color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const navigate    = useNavigate();
  const hoje        = new Date();
  const [hora, setHora]         = useState(new Date());
  const [dadosHoje, setDadosHoje] = useState(null);
  const [relatorio, setRelatorio] = useState({ registros: [], resumo: [] });
  const [mes, setMes]           = useState(hoje.getMonth() + 1);
  const [ano, setAno]           = useState(hoje.getFullYear());
  const [loading, setLoading]   = useState(true);
  const [hmDept, setHmDept]     = useState('');   // filtro setor heatmap
  const [modalAnalise, setModalAnalise] = useState(false);
  const [modalKpi, setModalKpi] = useState(null);
  const [modalKpiSetor, setModalKpiSetor] = useState(null);
  const [modalAnual, setModalAnual] = useState(false);
  const [anoAnual, setAnoAnual] = useState(hoje.getFullYear());
  const [dadosAnuais, setDadosAnuais] = useState(null);
  const [loadingAnual, setLoadingAnual] = useState(false);

  // busca anual
  useEffect(() => {
    if (!modalAnual) return;
    setLoadingAnual(true);
    setDadosAnuais(null);
    const ini = `01/01/${anoAnual}`;
    const fim = `31/12/${anoAnual}`;
    getRelatorio({ data_inicio: ini, data_fim: fim })
      .then(r => setDadosAnuais(r.data || { registros: [], resumo: [] }))
      .catch(() => setDadosAnuais({ registros: [], resumo: [] }))
      .finally(() => setLoadingAnual(false));
  }, [modalAnual, anoAnual]);

  // relógio
  useEffect(() => {
    const t = setInterval(() => setHora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // busca principal (KPIs, trend, ranking, alertas)
  useEffect(() => {
    setLoading(true);
    const fmt = d => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    const ini = new Date(ano, mes - 1, 1);
    const fim = new Date(ano, mes, 0);
    Promise.all([
      getDashboard(),
      getRelatorio({ data_inicio: fmt(ini), data_fim: fmt(fim) }),
    ])
      .then(([d, r]) => {
        setDadosHoje(d.data);
        setRelatorio(r.data || { registros: [], resumo: [] });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [mes, ano]);


  // ── Relatorio filtrado por setor ──────────────────────────────────────
  const relatorioFiltrado = useMemo(() => {
    if (!hmDept) return relatorio;
    const registros = (relatorio.registros || []).filter(r => r.departamento === hmDept);
    const funcsNoSetor = new Set(registros.map(r => r.funcionario));
    const resumo = (relatorio.resumo || []).filter(r => funcsNoSetor.has(r.funcionario));
    return { registros, resumo };
  }, [relatorio, hmDept]);

  // ── KPIs ──────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const { resumo, registros } = relatorioFiltrado;
    const totalExtras        = resumo.reduce((a, r) => a + r.total_extras, 0);
    const totalFaltas        = resumo.reduce((a, r) => a + r.faltas, 0);
    const totalDias          = resumo.reduce((a, r) => a + r.total_dias, 0);
    const totalHoras         = resumo.reduce((a, r) => a + r.total_horas, 0);
    const diasNormais        = registros.filter(r => r.status === 'NORMAL').length;
    const conformidade       = totalDias > 0 ? Math.round((diasNormais / totalDias) * 100) : 0;
    const mediaHoras         = totalDias > 0 ? (totalHoras / totalDias).toFixed(1) : '—';
    // funcionários do setor filtrado (quem tem registro no período)
    const totalFuncionarios  = hmDept
      ? resumo.filter(r => r.bate_ponto !== false).length
      : dadosHoje?.total_funcionarios ?? '—';
    // presentes hoje: só mostra quando sem filtro de setor
    const presentesHoje      = hmDept ? '—' : (dadosHoje?.presentes ?? '—');
    const insight =
      conformidade >= 90 ? { txt: 'Alta conformidade — risco trabalhista baixo',    cor: C.green  } :
      conformidade >= 75 ? { txt: 'Conformidade regular — risco trabalhista moderado', cor: C.yellow } :
                           { txt: 'Conformidade baixa — risco trabalhista alto',     cor: C.red    };
    return { totalExtras, totalFaltas, totalDias, totalHoras, conformidade, mediaHoras, insight, totalFuncionarios, presentesHoje };
  }, [relatorioFiltrado, dadosHoje, hmDept]);

  // ── Trend chart ──────────────────────────────────────────────────────
  const trendData = useMemo(() => {
    const byDate = {};
    for (const r of relatorioFiltrado.registros) {
      if (!byDate[r.data]) byDate[r.data] = { data: r.data, Horas: 0, Extras: 0, Faltas: 0 };
      byDate[r.data].Horas  += r.horas_trabalhadas;
      byDate[r.data].Extras += r.horas_extras;
      if (r.status === 'FALTA') byDate[r.data].Faltas += 1;
    }
    return Object.values(byDate)
      .sort((a, b) => sortByDate(a.data, b.data))
      .map(d => ({ ...d, data: fmtDia(d.data), Horas: +d.Horas.toFixed(1), Extras: +d.Extras.toFixed(1) }));
  }, [relatorioFiltrado]);

  // ── Ranking ───────────────────────────────────────────────────────────
  const CAMPOS_PONTO = ['hora_entrada','hora_saida_almoco','hora_retorno_almoco','hora_saida','hora_entrada3','hora_saida3','hora_entrada4','hora_saida4'];

  const ranking = useMemo(() => {
    // conta dias com ≥2 batidas por funcionário
    const diasTrabPorFunc = {};
    for (const r of relatorioFiltrado.registros) {
      const partidas = CAMPOS_PONTO.filter(c => r[c] && r[c] !== '—').length;
      if (partidas >= 2) {
        diasTrabPorFunc[r.funcionario] = (diasTrabPorFunc[r.funcionario] || 0) + 1;
      }
    }
    return relatorioFiltrado.resumo
      .filter(r => r.bate_ponto !== false)
      .map(r => {
        const diasComJornada  = r.total_dias;
        const diasPresentes   = Math.max(0, diasComJornada - r.faltas);
        const diasTrabalhados = diasTrabPorFunc[r.funcionario] || 0;
        const presenca        = diasComJornada > 0 ? Math.round((diasPresentes / diasComJornada) * 100) : 0;
        const score           = Math.max(0, Math.min(100, Math.round(100 - (r.faltas * 5) - (r.atestados * 2) + Math.min(diasComJornada * 0.4, 12))));
        return { ...r, score, presenca, diasComJornada, diasPresentes, diasTrabalhados };
      })
      .sort((a, b) => b.score - a.score);
  }, [relatorioFiltrado]);

  // ── Alertas ──────────────────────────────────────────────────────────
  const alertas = useMemo(() => {
    const list = [];
    for (const r of relatorioFiltrado.resumo) {
      if (r.faltas >= 3)
        list.push({ tipo: 'red', icon: 'bi-x-circle-fill', msg: `${r.funcionario}`, detail: `${r.faltas} faltas no mês` });
      if (r.total_extras > 10)
        list.push({ tipo: 'yellow', icon: 'bi-exclamation-triangle-fill', msg: `${r.funcionario}`, detail: `${toHHMM(r.total_extras)} em horas extras` });
      if (r.total_dias > 0 && (r.faltas / r.total_dias) > 0.15)
        list.push({ tipo: 'yellow', icon: 'bi-person-x-fill', msg: `${r.funcionario}`, detail: `Presença abaixo de 85%` });
    }
    return list;
  }, [relatorioFiltrado]);

  // ── KPIs por setor (dados completos, sem filtro) ─────────────────────
  const kpiPorSetor = useMemo(() => {
    const map = {};
    for (const r of relatorio.registros || []) {
      if (r.bate_ponto === false) continue;
      const s = r.departamento || 'Sem setor';
      if (!map[s]) map[s] = { setor: s, funcs: new Set(), diasTotal: 0, diasNormal: 0, extras: 0, faltas: 0, horas: 0 };
      map[s].funcs.add(r.funcionario);
      map[s].diasTotal++;
      map[s].horas  += r.horas_trabalhadas || 0;
      map[s].extras += r.horas_extras      || 0;
      if (r.status === 'FALTA')  map[s].faltas++;
      if (r.status === 'NORMAL') map[s].diasNormal++;
    }
    return Object.values(map).map(s => ({
      setor:         s.setor,
      funcs:         s.funcs.size,
      mediaHoras:    s.diasTotal > 0 ? +(s.horas / s.diasTotal).toFixed(1) : 0,
      extras:        s.extras,
      faltas:        s.faltas,
      conformidade:  s.diasTotal > 0 ? Math.round((s.diasNormal / s.diasTotal) * 100) : 0,
    })).sort((a, b) => a.setor.localeCompare(b.setor));
  }, [relatorio]);

  // ── Departamentos disponíveis no heatmap ──────────────────────────────
  const hmSetores = useMemo(() => {
    const set = new Set();
    for (const r of (relatorio.registros || [])) {
      if (r.departamento) set.add(r.departamento);
    }
    return [...set].sort();
  }, [relatorio]);

  // ── Heatmap ───────────────────────────────────────────────────────────
  const heatmap = useMemo(() => {
    const registros = relatorioFiltrado.registros || [];

    // Gera todos os dias do mês selecionado em dd/mm/yyyy
    const diasDoMes = [];
    const totalDias = new Date(ano, mes, 0).getDate();
    for (let d = 1; d <= totalDias; d++) {
      const dd = String(d).padStart(2, '0');
      const mm = String(mes).padStart(2, '0');
      diasDoMes.push(`${dd}/${mm}/${ano}`);
    }

    const byFunc = {};
    for (const r of registros) {
      // excluir quem não bate ponto
      if (r.bate_ponto === false) continue;
      if (!byFunc[r.funcionario]) byFunc[r.funcionario] = {};
      byFunc[r.funcionario][r.data] = r.status;
    }
    const employees = Object.keys(byFunc).sort();
    return { employees, dates: diasDoMes, matrix: byFunc };
  }, [relatorioFiltrado, mes, ano]);

  // ─────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div style={{ color: C.text, fontFamily: 'Inter, sans-serif' }}>

      {/* ── HEADER ── */}
      <div style={{ paddingBottom: 16, marginBottom: 20, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', flexShrink: 0 }}>
            <i className="bi bi-speedometer2" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Controle de Ponto</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Dashboard</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* seletor de mês */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px' }}>
            <i className="bi bi-calendar3" style={{ color: C.muted, fontSize: '0.8rem' }} />
            <select
              value={mes}
              onChange={e => setMes(Number(e.target.value))}
              style={{ background: 'transparent', border: 'none', color: C.text, fontSize: 'var(--fs-sm)', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
            >
              {MESES.map((m, i) => <option key={i} value={i+1} style={{ background: C.card }}>{m}</option>)}
            </select>
            <select
              value={ano}
              onChange={e => setAno(Number(e.target.value))}
              style={{ background: 'transparent', border: 'none', color: C.text, fontSize: 'var(--fs-sm)', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
            >
              {[2024, 2025, 2026].map(y => <option key={y} value={y} style={{ background: C.card }}>{y}</option>)}
            </select>
          </div>

          {/* filtros setor */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px' }}>
            <i className="bi bi-grid-3x3-gap-fill" style={{ color: C.muted, fontSize: '0.78rem' }} />
            <select
              value={hmDept}
              onChange={e => setHmDept(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: C.text, fontSize: 'var(--fs-sm)', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
            >
              <option value="">Todos os setores</option>
              {hmSetores.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* botão avaliação anual */}
          <button
            onClick={() => setModalAnual(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.indigo, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 13px', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            <i className="bi bi-calendar2-range-fill" style={{ fontSize: '0.75rem' }} />
            Avaliação Anual
          </button>

          {/* relógio */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '5px 14px', whiteSpace: 'nowrap' }}>
            <i className="bi bi-clock" style={{ fontSize: '0.65rem', color: C.blue, opacity: 0.8 }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: C.muted, textTransform: 'capitalize' }}>
              {hoje.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
            </span>
            <span style={{ color: C.border, fontSize: '0.75rem' }}>·</span>
            <span style={{ fontFamily: "'SF Mono', ui-monospace, monospace", fontSize: '0.75rem', fontWeight: 700, color: C.blue, letterSpacing: '0.5px', fontVariantNumeric: 'tabular-nums' }}>
              {hora.toLocaleTimeString('pt-BR')}
            </span>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 28px 0' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted }}>
            <div style={{ width: 32, height: 32, border: `3px solid #e2e8f0`, borderTopColor: C.blue, borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            Carregando dados...
          </div>
        )}

        {!loading && <>

          {/* ── INSIGHT CONFORMIDADE ── */}
          {kpis.conformidade > 0 && (
            <div style={{ background: `rgba(${kpis.conformidade >= 90 ? '34,197,94' : kpis.conformidade >= 75 ? '245,158,11' : '239,68,68'},0.07)`, border: `1px solid rgba(${kpis.conformidade >= 90 ? '34,197,94' : kpis.conformidade >= 75 ? '245,158,11' : '239,68,68'},0.18)`, borderRadius: 12, padding: '11px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <i className="bi bi-lightbulb-fill" style={{ color: kpis.insight.cor, fontSize: '0.9rem', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: kpis.insight.cor }}>{kpis.insight.txt}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: C.muted, marginLeft: 'auto' }}>
                {MESES[mes-1]} {ano} · {hmDept || 'Todos os setores'} · {relatorioFiltrado.resumo.length} colaboradores
              </span>
              <button
                onClick={() => setModalAnalise(true)}
                style={{ flexShrink: 0, background: kpis.insight.cor, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <i className="bi bi-bar-chart-fill" style={{ fontSize: 'var(--fs-xs)' }} />
                Ver análise
              </button>
            </div>
          )}

          {/* ── KPI CARDS + TREND + ALERTAS ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 24, alignItems: 'stretch' }}>

            {/* cards — coluna esquerda, mesma largura do gráfico */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 0 }}>
              {[
                { tipo: 'funcionarios', icon: 'bi-people-fill',        label: 'Funcionários Ativos', val: kpis.totalFuncionarios,  color: C.indigo, bg: '#ede9fe',  grad: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' },
                { tipo: 'presentes',    icon: 'bi-check-circle-fill',  label: 'Presentes Hoje',      val: kpis.presentesHoje,      color: C.green,  bg: '#dcfce7',  grad: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' },
                { tipo: 'extras',       icon: 'bi-lightning-fill',     label: 'Horas Extras/Mês',    val: toHHMM(kpis.totalExtras), color: C.yellow, bg: '#fef3c7', grad: 'linear-gradient(135deg,#fef3c7,#fde68a)' },
                { tipo: 'faltas',       icon: 'bi-x-circle-fill',      label: 'Faltas no Mês',       val: kpis.totalFaltas,         color: C.red,    bg: '#fee2e2',  grad: 'linear-gradient(135deg,#fee2e2,#fecaca)' },
                { tipo: 'conformidade', icon: 'bi-shield-check-fill',  label: 'Conformidade',        val: `${kpis.conformidade}%`,  color: kpis.conformidade >= 80 ? C.green : C.yellow, bg: kpis.conformidade >= 80 ? '#dcfce7' : '#fef3c7', grad: kpis.conformidade >= 80 ? 'linear-gradient(135deg,#dcfce7,#bbf7d0)' : 'linear-gradient(135deg,#fef3c7,#fde68a)' },
              ].map((k, i) => (
                <div key={i} className="kpi-card" onClick={() => setModalKpi(k.tipo)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 18px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110, cursor: 'pointer' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: k.color, borderRadius: '8px 8px 0 0' }} />
                  <div style={{ position: 'absolute', top: 14, right: 14, width: 36, height: 36, borderRadius: 10, background: k.grad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`bi ${k.icon}`} style={{ color: k.color, fontSize: '0.95rem' }} />
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 'clamp(1.3rem, 1.9vw, 1.7rem)', fontWeight: 900, color: C.text, letterSpacing: '-0.5px', lineHeight: 1 }}>{k.val}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: C.muted, marginTop: 6, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k.label}</div>
                    <div style={{ fontSize: '0.6rem', color: k.color, marginTop: 5, display: 'flex', alignItems: 'center', gap: 3, opacity: 0.75 }}>
                      <i className="bi bi-diagram-3" /><span>Ver por setor</span>
                    </div>
                  </div>
                  {i === 5 && kpis.conformidade > 0 && (
                    <div style={{ marginTop: 10, height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                      <div style={{ width: `${kpis.conformidade}%`, height: '100%', background: k.color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Alertas — ocupa as duas linhas da coluna direita */}
            <Card style={{ gridRow: 'span 2', display: 'flex', flexDirection: 'column' }}>
              <SectionTitle
                icon="bi-bell-fill"
                title="Alertas"
                sub={`${alertas.length} ocorrência${alertas.length !== 1 ? 's' : ''}`}
              />
              <div style={{ padding: '12px 14px', flex: 1, overflowY: 'auto' }}>
                {alertas.length === 0
                  ? (
                    <div style={{ textAlign: 'center', padding: '28px 0' }}>
                      <i className="bi bi-check-circle-fill" style={{ color: C.green, fontSize: '1.5rem', display: 'block', marginBottom: 8 }} />
                      <div style={{ fontSize: 'var(--fs-sm)', color: C.muted }}>Nenhum alerta no período</div>
                    </div>
                  )
                  : alertas.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < alertas.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: a.tipo === 'red' ? '#fee2e2' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={`bi ${a.icon}`} style={{ color: a.tipo === 'red' ? C.red : C.yellow, fontSize: '0.78rem' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{a.msg}</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: C.muted, marginTop: 2 }}>{a.detail}</div>
                      </div>
                    </div>
                  ))
                }
              </div>
              <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 14 }}>
                {[{ color: C.red, label: 'Crítico' }, { color: C.yellow, label: 'Atenção' }].map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-2xs)', color: C.muted }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: l.color }} />
                    {l.label}
                  </div>
                ))}
              </div>
            </Card>

          {/* ── TREND + ALERTAS (segunda linha do mesmo grid) ── */}

            {/* Gráfico */}
            <Card>
              <SectionTitle
                icon="bi-graph-up"
                title="Evolução do Mês"
                sub={`Horas trabalhadas, extras e faltas por dia — ${MESES[mes-1]} ${ano}`}
              />
              <div style={{ padding: '20px 16px 8px' }}>
                {trendData.length === 0
                  ? <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: '0.82rem' }}>Sem dados para o período</div>
                  : (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                        <defs>
                          <linearGradient id="gH" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={C.blue}   stopOpacity={0.25} />
                            <stop offset="95%" stopColor={C.blue}   stopOpacity={0}    />
                          </linearGradient>
                          <linearGradient id="gE" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={C.yellow} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={C.yellow} stopOpacity={0}    />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="data" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 'var(--fs-xs)', color: C.muted, paddingTop: 8 }} />
                        <Area type="monotone" dataKey="Horas"  stroke={C.blue}   strokeWidth={2} fill="url(#gH)" dot={false} />
                        <Area type="monotone" dataKey="Extras" stroke={C.yellow} strokeWidth={2} fill="url(#gE)" dot={false} />
                        <Area type="monotone" dataKey="Faltas" stroke={C.red}    strokeWidth={1.5} fill="none"   dot={false} strokeDasharray="4 3" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )
                }
              </div>
            </Card>

          </div>

          {/* ── RANKING ── */}
          <Card style={{ marginBottom: 24 }}>
            <SectionTitle
              icon="bi-trophy-fill"
              title="Ranking de Desempenho"
              sub="Score = 100 − (faltas × 5) − (atestados × 2) + bônus de presença"
            />
            <div style={{ overflowX: 'auto', padding: '0 18px 12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${C.border}` }}>
                    {['#', 'Código', 'Colaborador', 'Dias', 'Trabalhados', 'Presença', 'Faltas', 'H. Extras', 'Score', ''].map((h, i) => (
                      <th key={i} style={{ padding: '8px 10px', textAlign: i <= 2 ? 'left' : 'center', color: C.muted, fontWeight: 700, fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ranking.length === 0
                    ? <tr><td colSpan={10} style={{ textAlign: 'center', padding: '24px 0', color: C.muted, fontSize: '0.78rem' }}>Sem dados para o período</td></tr>
                    : ranking.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '5px 10px', textAlign: 'center', width: 32 }}>
                          {i === 0 ? <i className="bi bi-trophy-fill" style={{ color: '#FFD700', fontSize: '0.75rem' }} />
                          : i === 1 ? <i className="bi bi-trophy-fill" style={{ color: '#C0C0C0', fontSize: '0.75rem' }} />
                          : i === 2 ? <i className="bi bi-trophy-fill" style={{ color: '#CD7F32', fontSize: '0.75rem' }} />
                          : <span style={{ color: C.muted2, fontWeight: 600 }}>{i + 1}</span>}
                        </td>
                        <td style={{ padding: '5px 10px', color: C.muted, whiteSpace: 'nowrap' }}>{r.matricula || '—'}</td>
                        <td style={{ padding: '5px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, color: C.indigo, flexShrink: 0 }}>
                              {initials(r.funcionario)}
                            </div>
                            <span style={{ fontWeight: 600, color: C.text }}>{r.funcionario}</span>
                          </div>
                        </td>
                        <td style={{ padding: '5px 10px', color: C.muted, textAlign: 'center' }}>{r.total_dias}</td>
                        <td style={{ padding: '5px 10px', fontWeight: 600, color: C.text, textAlign: 'center' }}>{r.diasTrabalhados}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 40, height: 3, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                              <div style={{ width: `${r.presenca}%`, height: '100%', borderRadius: 2, background: r.presenca >= 90 ? C.green : r.presenca >= 75 ? C.yellow : C.red }} />
                            </div>
                            <span style={{ fontWeight: 700, color: r.presenca >= 90 ? C.green : r.presenca >= 75 ? C.yellow : C.red }}>{r.presenca}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                          {r.faltas > 0
                            ? <span style={{ background: 'rgba(239,68,68,0.1)', color: C.red, borderRadius: 3, padding: '1px 6px', fontWeight: 700 }}>{r.faltas}</span>
                            : <span style={{ color: C.muted }}>—</span>}
                        </td>
                        <td style={{ padding: '5px 10px', fontWeight: 600, color: r.total_extras > 0 ? C.yellow : C.muted, textAlign: 'center' }}>
                          {toHHMM(r.total_extras)}
                        </td>
                        <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                          <ScoreBadge score={r.score} />
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                          <button
                            title="Cartão de ponto individual"
                            onClick={() => navigate('/ponto-individual', { state: { funcionarioId: r.funcionario_id, mes, ano } })}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.indigo, padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#ede9fe'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          >
                            <i className="bi bi-person-lines-fill" style={{ fontSize: '0.85rem' }} />
                          </button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── HEATMAP ── */}
          <Card>
              <SectionTitle
                icon="bi-grid-3x3-gap-fill"
                title="Heatmap de Presença"
                sub={`${hmDept || 'Todos os setores'} · ${MESES[mes-1]} ${ano}`}
              />
              <div style={{ padding: '16px' }}>
                {/* header de datas */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 6, paddingLeft: 180 }}>
                  {heatmap.dates.map((d, i) => {
                    const [dd, mm, aa] = d.split('/');
                    const isDom = new Date(+aa, +mm - 1, +dd).getDay() === 0;
                    return (
                      <div key={i} style={{ flex: 1, minWidth: 0, fontSize: '0.55rem', textAlign: 'center', fontWeight: isDom ? 800 : 600, color: isDom ? '#dc2626' : C.muted }}>
                        {fmtDia(d).slice(0, 2)}
                      </div>
                    );
                  })}
                </div>
                {/* sem dados */}
                {heatmap.employees.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: C.muted, fontSize: '0.82rem' }}>
                    Sem registros para o período selecionado
                  </div>
                )}
                {/* linhas por funcionário */}
                {heatmap.employees.map((emp, ei) => (
                  <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 3 }}>
                    <div style={{ width: 176, flexShrink: 0, fontSize: 'var(--fs-xs)', fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 10, textAlign: 'right' }}>
                      {emp.split(' ').slice(0, 3).join(' ')}
                    </div>
                    {heatmap.dates.map((d, di) => {
                      const st = heatmap.matrix[emp]?.[d];
                      const [dd2, mm2, aa2] = d.split('/');
                      const isDomCell = new Date(+aa2, +mm2 - 1, +dd2).getDay() === 0;
                      return (
                        <div
                          key={di}
                          title={`${emp} · ${d} · ${st || 'sem registro'}`}
                          style={{
                            flex: 1, minWidth: 0, height: 22, borderRadius: 4, cursor: 'default',
                            background: st ? statusHeat(st) : isDomCell ? '#fecaca' : 'transparent',
                            border: st ? `1px solid rgba(0,0,0,0.08)` : isDomCell ? `1.5px solid #fca5a5` : `1.5px solid #cbd5e1`,
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
                {/* legenda */}
                <div style={{ display: 'flex', gap: 16, marginTop: 14, paddingLeft: 180 }}>
                  {[
                    { color: C.green,  label: 'Normal' },
                    { color: C.red,    label: 'Falta' },
                    { color: C.yellow, label: 'Atestado' },
                    { color: '#94a3b8', label: 'Folga/Feriado' },
                    { color: '#f1f5f9', label: 'Sem registro' },
                  ].map((l, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-2xs)', color: C.muted }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: l.color, border: `1px solid ${C.border}` }} />
                      {l.label}
                    </div>
                  ))}
                </div>
              </div>
            </Card>

        </>}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #1E293B; color: #F1F5F9; }
        .kpi-card { transition: box-shadow 0.15s ease, border-color 0.15s ease; }
        .kpi-card:hover { box-shadow: 0 4px 12px rgba(30,58,95,0.12) !important; border-color: #b0bec9 !important; }
        @keyframes kpiModalIn { from { opacity:0; transform:scale(0.96) translateY(8px) } to { opacity:1; transform:scale(1) translateY(0) } }
      `}</style>

      {/* ── MODAL KPI POR SETOR ── */}
      {modalKpi && (() => {
        const CFG = {
          funcionarios: { label: 'Funcionários Ativos',     icon: 'bi-people-fill',       color: C.indigo, fmtSetor: v => `${v}`,       sortKey: 'funcs',       listKey: 'total_dias',   listFmt: v => `${v} dias`,  listLabel: 'Dias trabalhados' },
          presentes:    { label: 'Funcionários no Período', icon: 'bi-check-circle-fill',  color: C.green,  fmtSetor: v => `${v}`,       sortKey: 'funcs',       listKey: 'total_dias',   listFmt: v => `${v} dias`,  listLabel: 'Dias trabalhados' },
          media_horas:  { label: 'Média Horas/Dia',         icon: 'bi-clock-fill',         color: C.blue,   fmtSetor: v => `${v}h`,      sortKey: 'mediaHoras',  listKey: 'total_horas',  listFmt: v => toHHMM(v),    listLabel: 'Top colaboradores — horas' },
          extras:       { label: 'Horas Extras/Mês',        icon: 'bi-lightning-fill',     color: C.yellow, fmtSetor: v => toHHMM(v),   sortKey: 'extras',      listKey: 'total_extras', listFmt: v => toHHMM(v),    listLabel: 'Top colaboradores — extras' },
          faltas:       { label: 'Faltas no Mês',           icon: 'bi-x-circle-fill',      color: C.red,    fmtSetor: v => `${v}`,       sortKey: 'faltas',      listKey: 'faltas',       listFmt: v => `${v} falta${v!==1?'s':''}`, listLabel: 'Colaboradores com faltas' },
          conformidade: { label: 'Conformidade',            icon: 'bi-shield-check-fill',  color: C.green,  fmtSetor: v => `${v}%`,      sortKey: 'conformidade',listKey: null,           listFmt: null,              listLabel: 'Colaboradores por setor' },
        };
        const cfg = CFG[modalKpi];

        // coluna esquerda — setores
        const rows = [...kpiPorSetor].sort((a, b) => b[cfg.sortKey] - a[cfg.sortKey]);
        const maxVal  = Math.max(...rows.map(r => r[cfg.sortKey]), 1);
        const totalGeral = rows.reduce((s, r) => s + r[cfg.sortKey], 0);

        // coluna direita — lista de colaboradores
        const res = relatorio.resumo.filter(r => r.bate_ponto !== false);
        const pessoas = cfg.listKey
          ? [...res].sort((a, b) => (b[cfg.listKey] || 0) - (a[cfg.listKey] || 0)).filter(r => (r[cfg.listKey] || 0) > 0).slice(0, 10)
          : [...res].map(r => {
              const reg = relatorio.registros.filter(x => x.funcionario === r.funcionario);
              const diasN = reg.filter(x => x.status === 'NORMAL').length;
              const conf  = reg.length > 0 ? Math.round((diasN / reg.length) * 100) : 0;
              return { ...r, _conf: conf };
            }).sort((a, b) => b._conf - a._conf).slice(0, 10);

        const maxPessoa = cfg.listKey
          ? Math.max(...pessoas.map(r => r[cfg.listKey] || 0), 1)
          : Math.max(...pessoas.map(r => r._conf || 0), 1);

        return (
          <div onClick={() => { setModalKpi(null); setModalKpiSetor(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, width: '100%', maxWidth: 820, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', animation: 'kpiModalIn 0.18s ease' }}>

              {/* header */}
              <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${cfg.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`bi ${cfg.icon}`} style={{ color: cfg.color, fontSize: '1rem' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 800, color: C.text }}>{cfg.label}</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: C.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{MESES[mes-1]} {ano} · Análise por setor</span>
                    {modalKpiSetor && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: `${cfg.color}18`, color: cfg.color, borderRadius: 10, padding: '1px 8px', fontSize: '0.65rem', fontWeight: 700 }}>
                        {modalKpiSetor}
                        <i className="bi bi-x" style={{ cursor: 'pointer' }} onClick={() => setModalKpiSetor(null)} />
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => { setModalKpi(null); setModalKpiSetor(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '1.1rem', padding: 4 }}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              {/* body — duas colunas */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>

                {/* esquerda — setores */}
                <div style={{ padding: '18px 20px', borderRight: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                    <i className="bi bi-diagram-3" style={{ marginRight: 5 }} />Por setor
                    {modalKpiSetor && <span style={{ marginLeft: 6, fontWeight: 400, fontSize: '0.6rem' }}>— clique para limpar</span>}
                  </div>
                  {rows.length === 0
                    ? <div style={{ color: C.muted, fontSize: 'var(--fs-xs)', fontStyle: 'italic' }}>Sem dados</div>
                    : rows.map((r, i) => {
                        const pct      = maxVal > 0 ? (r[cfg.sortKey] / maxVal) * 100 : 0;
                        const ativo    = modalKpiSetor === r.setor;
                        return (
                          <div
                            key={i}
                            onClick={() => setModalKpiSetor(ativo ? null : r.setor)}
                            style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: ativo ? `${cfg.color}12` : 'transparent', border: `1px solid ${ativo ? cfg.color+'44' : 'transparent'}`, transition: 'background 0.15s' }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {ativo && <i className="bi bi-funnel-fill" style={{ fontSize: '0.6rem', color: cfg.color }} />}
                                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: ativo ? cfg.color : C.text }}>{r.setor}</span>
                                <span style={{ fontSize: '0.58rem', color: C.muted2, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>{r.funcs} col.</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 800, color: cfg.color }}>{cfg.fmtSetor(r[cfg.sortKey])}</span>
                                {totalGeral > 0 && <span style={{ fontSize: '0.6rem', color: C.muted2, fontWeight: 600 }}>{Math.round((r[cfg.sortKey] / totalGeral) * 100)}%</span>}
                              </div>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 3, opacity: ativo ? 1 : 0.6, transition: 'width 0.4s ease, opacity 0.15s' }} />
                            </div>
                          </div>
                        );
                      })
                  }
                </div>

                {/* direita — lista de colaboradores (filtrada por setor se selecionado) */}
                <div style={{ padding: '18px 20px' }}>
                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                    <i className="bi bi-person-lines-fill" style={{ marginRight: 5 }} />{cfg.listLabel}
                    {modalKpiSetor && <span style={{ marginLeft: 4, fontWeight: 600, color: cfg.color }}>· {modalKpiSetor}</span>}
                  </div>
                  {(() => {
                    let lista = cfg.listKey
                      ? [...res].sort((a, b) => (b[cfg.listKey] || 0) - (a[cfg.listKey] || 0)).filter(r => (r[cfg.listKey] || 0) > 0)
                      : [...res].map(r => {
                          const reg = relatorio.registros.filter(x => x.funcionario === r.funcionario);
                          const diasN = reg.filter(x => x.status === 'NORMAL').length;
                          return { ...r, _conf: reg.length > 0 ? Math.round((diasN / reg.length) * 100) : 0 };
                        }).sort((a, b) => b._conf - a._conf);

                    if (modalKpiSetor) {
                      const funcsDoSetor = new Set(
                        relatorio.registros.filter(r => r.departamento === modalKpiSetor).map(r => r.funcionario)
                      );
                      lista = lista.filter(r => funcsDoSetor.has(r.funcionario));
                    }

                    lista = lista.slice(0, 12);
                    const maxP = cfg.listKey
                      ? Math.max(...lista.map(r => r[cfg.listKey] || 0), 1)
                      : Math.max(...lista.map(r => r._conf || 0), 1);
                    const totalP = cfg.listKey
                      ? lista.reduce((s, r) => s + (r[cfg.listKey] || 0), 0)
                      : 0;

                    if (lista.length === 0) return <div style={{ color: C.muted, fontSize: 'var(--fs-xs)', fontStyle: 'italic' }}>Nenhum resultado</div>;

                    return lista.map((r, i) => {
                      const rawVal = cfg.listKey ? (r[cfg.listKey] || 0) : r._conf;
                      const pct    = maxP > 0 ? (rawVal / maxP) * 100 : 0;
                      const fmtVal = cfg.listKey ? cfg.listFmt(rawVal) : `${r._conf}%`;
                      const nome   = r.funcionario.split(' ').slice(0, 2).join(' ');
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <div style={{ width: 26, height: 26, borderRadius: 7, background: `${cfg.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.6rem', fontWeight: 800, color: cfg.color }}>{initials(r.funcionario)}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nome}</span>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0, marginLeft: 6 }}>
                                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: cfg.color }}>{fmtVal}</span>
                                {cfg.listKey && totalP > 0 && <span style={{ fontSize: '0.58rem', color: C.muted2, fontWeight: 600 }}>{Math.round(((r[cfg.listKey] || 0) / totalP) * 100)}%</span>}
                              </div>
                            </div>
                            <div style={{ height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 2, transition: 'width 0.4s ease' }} />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

              </div>
            </div>
          </div>
        );
      })()}

      {/* ── MODAL ANÁLISE DE CONFORMIDADE ── */}
      {modalAnalise && (() => {
        const reg = relatorioFiltrado.registros;
        const res = relatorioFiltrado.resumo.filter(r => r.bate_ponto !== false);
        const totalDias   = kpis.totalDias;
        const diasNormal  = reg.filter(r => r.status === 'NORMAL').length;
        const diasFalta   = reg.filter(r => r.status === 'FALTA').length;
        const diasAtestado= reg.filter(r => r.status === 'ATESTADO').length;
        const diasFolga   = reg.filter(r => r.status === 'FOLGA').length;
        const diasFerias  = reg.filter(r => r.status === 'FERIAS').length;
        const diasFeriado = reg.filter(r => r.status === 'FERIADO').length;
        const cor = kpis.conformidade >= 90 ? C.green : kpis.conformidade >= 75 ? C.yellow : C.red;
        const bgCor = kpis.conformidade >= 90 ? 'rgba(34,197,94,0.08)' : kpis.conformidade >= 75 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)';

        const topFaltas  = [...res].sort((a,b) => b.faltas - a.faltas).filter(r => r.faltas > 0).slice(0, 5);
        const topExtras  = [...res].sort((a,b) => b.total_extras - a.total_extras).filter(r => r.total_extras > 0).slice(0, 5);
        const maxFaltas  = topFaltas[0]?.faltas || 1;
        const maxExtras  = topExtras[0]?.total_extras || 1;

        const StatusBar = ({ label, count, total, color }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ width: 90, fontSize: 'var(--fs-xs)', color: C.muted, flexShrink: 0 }}>{label}</span>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background: C.border, overflow: 'hidden' }}>
              <div style={{ width: `${total > 0 ? (count/total)*100 : 0}%`, height: '100%', background: color, borderRadius: 4 }} />
            </div>
            <span style={{ width: 50, fontSize: 'var(--fs-xs)', fontWeight: 700, color, textAlign: 'right' }}>
              {count} <span style={{ fontWeight: 400, color: C.muted }}>({total > 0 ? Math.round(count/total*100) : 0}%)</span>
            </span>
          </div>
        );

        return (
          <div
            onClick={() => setModalAnalise(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: C.card, borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            >
              {/* header modal */}
              <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: bgCor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-bar-chart-fill" style={{ color: cor, fontSize: '1rem' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: C.text }}>Análise de Conformidade</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>{MESES[mes-1]} {ano} · {hmDept || 'Todos os setores'} · {res.length} colaboradores</div>
                </div>
                <button onClick={() => setModalAnalise(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '1.1rem', padding: 4 }}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <div style={{ padding: '20px 24px' }}>
                {/* score destaque */}
                <div style={{ background: bgCor, border: `1px solid ${cor}33`, borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2.4rem', fontWeight: 900, color: cor, lineHeight: 1 }}>{kpis.conformidade}%</div>
                    <div style={{ fontSize: 'var(--fs-2xs)', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>Conformidade</div>
                  </div>
                  <div style={{ width: 1, height: 50, background: C.border }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: cor, marginBottom: 4 }}>{kpis.insight.txt}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>
                      {kpis.conformidade >= 90
                        ? 'O índice de presença está excelente. Risco de passivo trabalhista é baixo.'
                        : kpis.conformidade >= 75
                        ? 'Atenção ao índice de faltas. Acompanhe os colaboradores com maior ausência.'
                        : 'Índice crítico. Verifique os colaboradores com faltas recorrentes imediatamente.'}
                    </div>
                  </div>
                </div>

                {/* distribuição de status */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Distribuição de dias</div>
                  <StatusBar label="Normal"   count={diasNormal}   total={totalDias} color={C.green}  />
                  <StatusBar label="Falta"    count={diasFalta}    total={totalDias} color={C.red}    />
                  <StatusBar label="Atestado" count={diasAtestado} total={totalDias} color={C.yellow} />
                  <StatusBar label="Folga"    count={diasFolga}    total={totalDias} color={C.muted2} />
                  <StatusBar label="Férias"   count={diasFerias}   total={totalDias} color={C.blue}   />
                  <StatusBar label="Feriado"  count={diasFeriado}  total={totalDias} color={C.cyan}   />
                  <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 8, paddingTop: 8, display: 'flex', gap: 24 }}>
                    <span style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>Total de dias: <strong style={{ color: C.text }}>{totalDias}</strong></span>
                    <span style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>Total horas: <strong style={{ color: C.text }}>{toHHMM(kpis.totalHoras)}</strong></span>
                    <span style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>Média/dia: <strong style={{ color: C.text }}>{kpis.mediaHoras}h</strong></span>
                  </div>
                </div>

                {/* top faltas + top extras */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* top faltas */}
                  <div>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      <i className="bi bi-x-circle-fill" style={{ color: C.red, marginRight: 5 }} />Maiores ausências
                    </div>
                    {topFaltas.length === 0
                      ? <div style={{ fontSize: 'var(--fs-xs)', color: C.muted, fontStyle: 'italic' }}>Nenhuma falta no período</div>
                      : topFaltas.map((r, i) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 'var(--fs-xs)', color: C.text, fontWeight: 600 }}>{r.funcionario.split(' ').slice(0,2).join(' ')}</span>
                            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.red }}>{r.faltas} falta{r.faltas > 1 ? 's' : ''}</span>
                          </div>
                          <div style={{ height: 5, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                            <div style={{ width: `${(r.faltas/maxFaltas)*100}%`, height: '100%', background: C.red, borderRadius: 3 }} />
                          </div>
                        </div>
                      ))
                    }
                  </div>

                  {/* top horas extras */}
                  <div>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                      <i className="bi bi-lightning-fill" style={{ color: C.yellow, marginRight: 5 }} />Mais horas extras
                    </div>
                    {topExtras.length === 0
                      ? <div style={{ fontSize: 'var(--fs-xs)', color: C.muted, fontStyle: 'italic' }}>Nenhuma hora extra no período</div>
                      : topExtras.map((r, i) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 'var(--fs-xs)', color: C.text, fontWeight: 600 }}>{r.funcionario.split(' ').slice(0,2).join(' ')}</span>
                            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.yellow }}>{toHHMM(r.total_extras)}</span>
                          </div>
                          <div style={{ height: 5, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                            <div style={{ width: `${(r.total_extras/maxExtras)*100}%`, height: '100%', background: C.yellow, borderRadius: 3 }} />
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── MODAL AVALIAÇÃO ANUAL ── */}
      {modalAnual && (() => {
        const mesesNomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
        const anos = [hoje.getFullYear() - 2, hoje.getFullYear() - 1, hoje.getFullYear()];

        const porMes = Array.from({ length: 12 }, (_, i) => ({
          mes: i + 1, label: mesesNomes[i],
          diasTotal: 0, diasNormal: 0, extras: 0, faltas: 0, horas: 0, funcs: new Set(),
        }));

        if (dadosAnuais) {
          for (const r of dadosAnuais.registros || []) {
            if (r.bate_ponto === false) continue;
            const mm = parseInt((r.data || '').split('/')[1], 10);
            if (!mm || mm < 1 || mm > 12) continue;
            const m = porMes[mm - 1];
            m.funcs.add(r.funcionario);
            m.diasTotal++;
            m.horas  += r.horas_trabalhadas || 0;
            m.extras += r.horas_extras      || 0;
            if (r.status === 'FALTA')  m.faltas++;
            if (r.status === 'NORMAL') m.diasNormal++;
          }
        }

        const mesesComDados = porMes.map(m => ({
          ...m,
          funcs:        m.funcs.size,
          mediaHoras:   m.diasTotal > 0 ? +(m.horas / m.diasTotal).toFixed(1) : 0,
          conformidade: m.diasTotal > 0 ? Math.round((m.diasNormal / m.diasTotal) * 100) : null,
        }));

        const maxExtras    = Math.max(...mesesComDados.map(m => m.extras), 1);
        const maxFaltas    = Math.max(...mesesComDados.map(m => m.faltas), 1);
        const totalExtrasAno = mesesComDados.reduce((s, m) => s + m.extras, 0);
        const totalFaltasAno = mesesComDados.reduce((s, m) => s + m.faltas, 0);
        const mediaCfArr     = mesesComDados.filter(m => m.conformidade !== null);
        const mediaConfAno   = mediaCfArr.length > 0 ? Math.round(mediaCfArr.reduce((s, m) => s + m.conformidade, 0) / mediaCfArr.length) : 0;
        const corConf = v => v === null ? C.muted2 : v >= 90 ? C.green : v >= 75 ? C.yellow : C.red;

        return (
          <div onClick={() => setModalAnual(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', animation: 'kpiModalIn 0.18s ease' }}>

              {/* header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-calendar2-range-fill" style={{ color: C.indigo, fontSize: '1rem' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: C.text }}>Avaliação Anual</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: C.muted }}>Visão consolidada mês a mês</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px' }}>
                  <i className="bi bi-calendar3" style={{ color: C.muted, fontSize: '0.8rem' }} />
                  <select value={anoAnual} onChange={e => setAnoAnual(Number(e.target.value))} style={{ background: 'transparent', border: 'none', color: C.text, fontSize: 'var(--fs-sm)', fontWeight: 700, outline: 'none', cursor: 'pointer' }}>
                    {anos.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button onClick={() => setModalAnual(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '1.1rem', padding: 4 }}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              {/* totalizadores */}
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { icon: 'bi-lightning-fill',    label: 'Total Horas Extras',  val: toHHMM(totalExtrasAno), color: C.yellow, bg: '#fef3c7' },
                  { icon: 'bi-x-circle-fill',     label: 'Total Faltas',        val: totalFaltasAno,          color: C.red,    bg: '#fee2e2' },
                  { icon: 'bi-shield-check-fill', label: 'Conformidade Média',  val: `${mediaConfAno}%`,      color: corConf(mediaConfAno), bg: mediaConfAno >= 90 ? '#dcfce7' : mediaConfAno >= 75 ? '#fef3c7' : '#fee2e2' },
                ].map((k, i) => (
                  <div key={i} style={{ background: k.bg, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <i className={`bi ${k.icon}`} style={{ color: k.color, fontSize: '1.1rem', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 900, color: k.color, lineHeight: 1 }}>{k.val}</div>
                      <div style={{ fontSize: 'var(--fs-2xs)', color: k.color, opacity: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{k.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* tabela */}
              <div style={{ padding: '0 24px 24px' }}>
                {loadingAnual ? (
                  <div style={{ textAlign: 'center', padding: '48px 0', color: C.muted }}>
                    <div style={{ width: 28, height: 28, border: `3px solid #e2e8f0`, borderTopColor: C.indigo, borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
                    Carregando {anoAnual}...
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                        {['Mês','Colaboradores','Dias Trab.','H. Extras','% do ano','Faltas','% do ano','Conformidade'].map((h, i) => (
                          <th key={i} style={{ padding: '8px 8px', fontSize: 'var(--fs-2xs)', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: i === 0 ? 'left' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mesesComDados.map((m, i) => {
                        const semDados   = m.diasTotal === 0;
                        const pctExtras  = totalExtrasAno > 0 ? Math.round((m.extras / totalExtrasAno) * 100) : 0;
                        const pctFaltas  = totalFaltasAno > 0 ? Math.round((m.faltas / totalFaltasAno) * 100) : 0;
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.bg, opacity: semDados ? 0.4 : 1 }}>
                            <td style={{ padding: '10px 8px', fontSize: 'var(--fs-sm)', fontWeight: 700, color: C.text }}>{m.label}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: 'var(--fs-xs)', color: C.muted }}>{semDados ? '—' : m.funcs}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: 'var(--fs-xs)', color: C.muted }}>{semDados ? '—' : m.diasTotal}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                              {semDados ? <span style={{ color: C.muted, fontSize: 'var(--fs-xs)' }}>—</span> : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <div style={{ width: 44, height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                                    <div style={{ width: `${(m.extras / maxExtras) * 100}%`, height: '100%', background: C.yellow, borderRadius: 2 }} />
                                  </div>
                                  <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.yellow }}>{toHHMM(m.extras)}</span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: 'var(--fs-xs)', color: C.muted2, fontWeight: 600 }}>
                              {semDados ? '—' : `${pctExtras}%`}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                              {semDados ? <span style={{ color: C.muted, fontSize: 'var(--fs-xs)' }}>—</span> : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <div style={{ width: 44, height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                                    <div style={{ width: `${(m.faltas / maxFaltas) * 100}%`, height: '100%', background: C.red, borderRadius: 2 }} />
                                  </div>
                                  <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.red }}>{m.faltas}</span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: 'var(--fs-xs)', color: C.muted2, fontWeight: 600 }}>
                              {semDados ? '—' : `${pctFaltas}%`}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                              {m.conformidade === null ? <span style={{ color: C.muted, fontSize: 'var(--fs-xs)' }}>—</span> : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <div style={{ width: 44, height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                                    <div style={{ width: `${m.conformidade}%`, height: '100%', background: corConf(m.conformidade), borderRadius: 2 }} />
                                  </div>
                                  <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 800, color: corConf(m.conformidade) }}>{m.conformidade}%</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
